import React from "react";
import { Platform, StyleSheet, View } from "react-native";
import {
  ArrowClockwiseIcon,
  ArrowRightIcon,
  LinkIcon,
  LockSimpleIcon,
  QuestionIcon,
  SparkleIcon,
  TrendUpIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { LinearGradient } from "expo-linear-gradient";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomProgressBar from "../../../../../components/common/general/CustomProgressBar/CustomProgressBar";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import Pill from "../../../../../components/common/Pill/Pill";
import type {
  CourseEvaluatorReviewCategory,
  CourseEvaluatorReviewDocumentIssueKind,
  CourseEvaluatorReviewEffectiveness,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewStage,
} from "../types/reviewTypes";
import ScoreGauge from "./ScoreGauge";
import GapAnalysis from "./GapAnalysis";
import { colors } from "../../../../../styles/design-system";

interface OverviewPanelProps {
  review: CourseEvaluatorReviewData;
  onStartReviewing: () => void;
  onOverlayVisibilityChange?: (isVisible: boolean) => void;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAcceptWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onRejectWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
}

function getEffectivenessPill(effectiveness: CourseEvaluatorReviewEffectiveness) {
  switch (effectiveness) {
    case "excellent":
      return { label: "Excellent", backgroundColor: "#DFF7E7", textColor: "#15703D" };
    case "fair":
      return { label: "Fair", backgroundColor: "#FFF3D6", textColor: "#B54708" };
    case "poor":
      return { label: "Poor", backgroundColor: "#FEE4E2", textColor: "#B42318" };
    case "good":
    default:
      return { label: "Good", backgroundColor: "#DCE7FF", textColor: "#1F3B8F" };
  }
}

function getReviewStageLabel(reviewStage: CourseEvaluatorReviewStage) {
  switch (reviewStage) {
    case "content-review":
      return "Content Review";
    case "full-course-review":
    default:
      return "Full Course Review";
  }
}

function getCategoryColors(category: CourseEvaluatorReviewCategory) {
  const percentage = category.maxScore > 0 ? (category.score / category.maxScore) * 100 : 0;

  if (percentage >= 80) {
    return {
      barColor: "#62C61A",
      scoreColor: "#3F7F18",
    };
  }

  if (percentage >= 60) {
    return {
      barColor: "#3E68FE",
      scoreColor: "#3E68FE",
    };
  }

  if (percentage >= 40) {
    return {
      barColor: "#DC6803",
      scoreColor: "#DC6803",
    };
  }

  return {
    barColor: "#D92D20",
    scoreColor: "#D92D20",
  };
}

function renderDocumentIssueIcon(documentIssueKind: CourseEvaluatorReviewDocumentIssueKind) {
  if (documentIssueKind === "permissionDenied") {
    return <LockSimpleIcon size={18} color="#DC6803" weight="bold" style={{ flexShrink: 0 }} />;
  }

  return <LinkIcon size={18} color="#DC6803" weight="bold" style={{ flexShrink: 0 }} />;
}

function OverviewPanel({
  review,
  onStartReviewing,
  onOverlayVisibilityChange,
  onAccept,
  onReject,
  onAcceptWithoutNav,
  onRejectWithoutNav,
  onJumpToEntity,
}: OverviewPanelProps) {
  const effectivenessPill = getEffectivenessPill(review.effectiveness);

  return (
    <View style={styles.panel}>
      <GapAnalysis
        review={review}
        onStartReviewing={onStartReviewing}
        onOverlayVisibilityChange={onOverlayVisibilityChange}
        onAccept={onAccept}
        onReject={onReject}
        onAcceptWithoutNav={onAcceptWithoutNav}
        onRejectWithoutNav={onRejectWithoutNav}
        onJumpToEntity={onJumpToEntity}
      />

      {review.documentIssues.length > 0 && (
        <View style={styles.card}>
          <View style={styles.documentHeader}>
            <WarningCircleIcon size={20} color="#DC6803" weight="fill" />
            <CustomText
              text={`${review.documentIssues.length} documents couldn't be accessed`}
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.documentHeaderText}
            />
          </View>

          <View style={styles.documentList}>
            {review.documentIssues.map((documentIssue) => (
              <View key={documentIssue.id} style={styles.documentIssueCard}>
                {renderDocumentIssueIcon(documentIssue.documentIssueKind)}
                <View style={styles.documentTextWrap}>
                  <CustomText
                    text={documentIssue.title}
                    useTranslationText={false}
                    size="s"
                    weight="normal"
                    style={styles.documentTitle}
                  />
                  <CustomText
                    text={documentIssue.message}
                    useTranslationText={false}
                    size="xs"
                    weight="normal"
                    style={styles.documentMessage}
                  />
                </View>
                <CustomButton
                  text="Retry"
                  useTranslationText={false}
                  onPress={() => {}}
                  styleType="transparent"
                  size="s"
                  leftIcon={<ArrowClockwiseIcon size={16} color={colors.grey[700]} />}
                  textStyle={styles.retryText}
                  style={styles.retryButton}
                />
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: 16,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 20,
  },
  documentHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  documentHeaderText: {
    color: "#DC6803",
  },
  documentList: {
    gap: 10,
  },
  documentIssueCard: {
    backgroundColor: "#FFF8E8",
    borderWidth: 1,
    borderColor: "#FCE7B0",
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  documentTextWrap: {
    flex: 1,
  },
  documentTitle: {
    color: colors.grey[800],
    marginBottom: 2,
  },
  documentMessage: {
    color: colors.grey[500],
  },
  retryButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  retryText: {
    color: colors.grey[700],
    fontSize: 12,
    fontWeight: "500",
  },
});

export default OverviewPanel;
